/**
 * Bulk ★ selection logic for the Compare rail.
 *
 * The rail's ★ shortlist is URL state (`?stars=`) and capped at MAX_STARS; the
 * multi-select tick boxes are ephemeral page state. Everything that decides
 * WHICH ids get starred lives here (pure), so the cap and the ordering are
 * testable without mounting the page.
 *
 * Ordering rule: a bulk star APPENDS in the rail's visible order and never
 * reorders the stars already there — the starred view sorts by match count
 * anyway, but a stable append keeps `?stars=` diffs readable when shared.
 */

export interface BulkStarResult {
  /** The next `?stars=` value, already capped. */
  stars: number[];
  /** How many of `ids` were newly starred. */
  added: number;
  /** Already-starred ids among `ids` (no-ops, not an error). */
  alreadyStarred: number;
  /** Ids dropped because the shortlist hit its cap — the number to warn about. */
  skipped: number;
}

/** Star every id in `ids` at once, honouring the shortlist cap.
 *  Existing stars are preserved and keep their position. */
export function applyBulkStar(stars: number[], ids: number[], max: number): BulkStarResult {
  const current = new Set(stars);
  const next = [...stars];
  let added = 0;
  let alreadyStarred = 0;
  let skipped = 0;
  for (const id of ids) {
    if (current.has(id)) {
      alreadyStarred += 1;
      continue;
    }
    if (next.length >= max) {
      skipped += 1;
      continue;
    }
    current.add(id);
    next.push(id);
    added += 1;
  }
  return { stars: next, added, alreadyStarred, skipped };
}

/** Remove every id in `ids` from the shortlist, keeping the rest in order. */
export function applyBulkUnstar(stars: number[], ids: number[]): number[] {
  const drop = new Set(ids);
  return stars.filter((id) => !drop.has(id));
}

/** Toggle one id in the ephemeral selection set (returns a new Set).
 *  Adding is REFUSED at `max` — a selection you could not star in full is a
 *  trap, so the cap is enforced where the user clicks, not at star time.
 *  Unticking always works, cap or no cap. */
export function toggleSelected(selected: Set<number>, id: number, max: number): Set<number> {
  if (selected.has(id)) {
    const next = new Set(selected);
    next.delete(id);
    return next;
  }
  if (selected.size >= max) return selected;
  return new Set(selected).add(id);
}

export interface SelectAllResult {
  selected: Set<number>;
  /** Rows left out because the cap was reached — what to warn about. */
  skipped: number;
}

/** "Select all": take the visible rows in order, up to `max`. */
export function selectAllUpTo(visibleIds: number[], max: number): SelectAllResult {
  return {
    selected: new Set(visibleIds.slice(0, max)),
    skipped: Math.max(0, visibleIds.length - max),
  };
}

/** The standing note under the bulk bar while the selection is at its cap. */
export function selectionCapNote(selectedCount: number, max: number): string | null {
  return selectedCount >= max
    ? `Selection limit reached (${max}) — untick a row to pick another.`
    : null;
}

/** One-line summary for the bulk bar after a star action. */
export function describeBulkStar(r: BulkStarResult, max: number): string | null {
  if (r.skipped > 0) {
    return r.added > 0
      ? `Starred ${r.added} — ${r.skipped} skipped (limit ${max}).`
      : `Starred list is full (${max}) — ${r.skipped} not starred.`;
  }
  if (r.added > 0) return `Starred ${r.added}.`;
  if (r.alreadyStarred > 0) return 'Already starred.';
  return null;
}
